import React, { useState, useEffect, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from 'react-joyride';
import styled from 'styled-components';
import { theme } from '../design-system/theme';
import { PrimaryButton, SecondaryButton, ModalOverlay } from '../styles/adminShared';

// Storage keys
const TOUR_STORAGE_KEY = 'consent_platform_tour_completed';
const TOUR_DISMISSED_KEY = 'consent_platform_tour_dismissed';

// Check if tour has been completed or dismissed
export const hasCompletedTour = (): boolean => {
  return localStorage.getItem(TOUR_STORAGE_KEY) === 'true' || 
         localStorage.getItem(TOUR_DISMISSED_KEY) === 'true';
};

export const markTourCompleted = (): void => {
  localStorage.setItem(TOUR_STORAGE_KEY, 'true');
};

export const markTourDismissed = (): void => {
  localStorage.setItem(TOUR_DISMISSED_KEY, 'true');
};

export const resetTourState = (): void => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  localStorage.removeItem(TOUR_DISMISSED_KEY);
};

// Welcome modal styles
const WelcomeModal = styled.div`
  background: white;
  border-radius: 20px;
  padding: 48px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const WelcomeIcon = styled.div`
  font-size: 72px;
  margin-bottom: 24px;
`;

const WelcomeTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 28px;
  font-weight: 700;
  color: ${theme.textPrimary};
`;

const WelcomeText = styled.p`
  margin: 0 0 32px 0;
  font-size: 16px;
  color: ${theme.textSecondary};
  line-height: 1.6;
`;

const WelcomeButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`;

// Tour steps
const tourSteps: Step[] = [
  {
    target: '[data-tour="sidebar"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          👋 Welcome to Consent Platform
        </h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#64748b' }}>
          This is your navigation sidebar. Use it to access all features of the consent management platform.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-templates"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          📋 Templates
        </h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#64748b' }}>
          Create and manage consent templates. Templates define what data you collect and how consent is displayed.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="nav-consent-forms"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          🔗 Consent URLs
        </h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#64748b' }}>
          Generate shareable consent collection URLs. Send these to data owners via email or SMS.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="nav-consents"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          ✅ Consent Artifacts
        </h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#64748b' }}>
          View all collected consents, their status, and evidence. Export records for compliance reporting.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="nav-analytics"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          📈 Analytics
        </h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#64748b' }}>
          Track consent trends, conversion rates, and usage statistics with interactive charts.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="user-menu"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          👤 Your Profile
        </h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#64748b' }}>
          Access your profile settings, organization details, and logout from here.
        </p>
        <p style={{ margin: '12px 0 0 0', fontSize: '14px', fontWeight: 600, color: '#22c55e' }}>
          🎉 You're all set! Start by creating your first template.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
];

// Joyride custom styles
const joyrideStyles = {
  options: {
    primaryColor: '#6366f1',
    zIndex: 10000,
    arrowColor: '#fff',
    backgroundColor: '#fff',
    textColor: '#1e293b',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
  },
  tooltip: {
    borderRadius: 16,
    padding: 20,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  buttonNext: {
    backgroundColor: '#6366f1',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
  },
  buttonBack: {
    color: '#64748b',
    marginRight: 10,
    fontSize: 14,
  },
  buttonSkip: {
    color: '#94a3b8',
    fontSize: 14,
  },
  buttonClose: {
    display: 'none',
  },
  spotlight: {
    borderRadius: 12,
  },
};

// Welcome Prompt Props
interface WelcomePromptProps {
  onStartTour: () => void;
  onSkip: () => void;
}

export const WelcomePrompt: React.FC<WelcomePromptProps> = ({ onStartTour, onSkip }) => {
  return (
    <ModalOverlay $show={true}>
      <WelcomeModal>
        <WelcomeIcon>🚀</WelcomeIcon>
        <WelcomeTitle>Welcome to Consent Platform!</WelcomeTitle>
        <WelcomeText>
          Let us show you around! Take a quick 30-second tour to learn 
          how to manage consents, create templates, and stay compliant.
        </WelcomeText>
        <WelcomeButtons>
          <SecondaryButton onClick={onSkip}>
            Maybe Later
          </SecondaryButton>
          <PrimaryButton onClick={onStartTour}>
            🎯 Start Tour
          </PrimaryButton>
        </WelcomeButtons>
      </WelcomeModal>
    </ModalOverlay>
  );
};

// Tour Context
interface TourContextType {
  runTour: boolean;
  setRunTour: (run: boolean) => void;
  showWelcome: boolean;
  startTour: () => void;
  skipTour: () => void;
  restartTour: () => void;
}

const TourContext = React.createContext<TourContextType | undefined>(undefined);

// Onboarding Provider
interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const [runTour, setRunTour] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Check for new user on mount
  useEffect(() => {
    const isNewUser = !hasCompletedTour();
    if (isNewUser && window.location.pathname.includes('/dashboard')) {
      const timer = setTimeout(() => setShowWelcome(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Check for tour=start query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tour') === 'start') {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => {
        setRunTour(true);
        setStepIndex(0);
      }, 500);
    }
  }, []);

  const startTour = useCallback(() => {
    setShowWelcome(false);
    // Navigate to dashboard if not there
    if (!window.location.pathname.includes('/dashboard')) {
      const tenantSlug = localStorage.getItem('tenantSlug') || 'globalhealth';
      window.location.href = `/t/${tenantSlug}/dashboard?tour=start`;
      return;
    }
    setStepIndex(0);
    setRunTour(true);
  }, []);

  const skipTour = useCallback(() => {
    setShowWelcome(false);
    markTourDismissed();
  }, []);

  const restartTour = useCallback(() => {
    resetTourState();
    // Navigate to dashboard if not there
    if (!window.location.pathname.includes('/dashboard')) {
      const tenantSlug = localStorage.getItem('tenantSlug') || 'globalhealth';
      window.location.href = `/t/${tenantSlug}/dashboard?tour=start`;
      return;
    }
    setStepIndex(0);
    setRunTour(true);
  }, []);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    
    if (finishedStatuses.includes(status)) {
      setRunTour(false);
      if (status === STATUS.FINISHED) {
        markTourCompleted();
      } else {
        markTourDismissed();
      }
    }
    
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  }, []);

  return (
    <TourContext.Provider value={{ runTour, setRunTour, showWelcome, startTour, skipTour, restartTour }}>
      {children}
      
      {showWelcome && (
        <WelcomePrompt onStartTour={startTour} onSkip={skipTour} />
      )}
      
      <Joyride
        steps={tourSteps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        spotlightClicks
        disableOverlayClose
        styles={joyrideStyles}
        callback={handleJoyrideCallback}
        locale={{
          back: '← Back',
          close: 'Close',
          last: 'Finish 🎉',
          next: 'Next →',
          skip: 'Skip tour',
        }}
      />
    </TourContext.Provider>
  );
};

// Hook to use the tour
export const useOnboardingTour = () => {
  const context = React.useContext(TourContext);
  if (!context) {
    throw new Error('useOnboardingTour must be used within OnboardingProvider');
  }
  return context;
};

export default OnboardingProvider;
